<template>
  <div :class="['frequency-capping', { desktop: isDesktop }]">
    <el-form-item
      class="frequency-capping-item"
      :error="errors.refresh_rate"
      label="Frequency Capping"
    >
      <template #label>
        <span>
          Frequency Capping
          <IconWithHint>
            <p v-html="hint" />
          </IconWithHint>
        </span>
      </template>
      <div class="input-hint">{{ dynamicHintText }}</div>
      <div class="radio-wrapper">
        <el-radio-group v-model="refreshRate" @update:modelValue="input">
          <el-radio-button :value="0">None</el-radio-button>
          <el-radio-button :value="86400">1 imp / 24 h</el-radio-button>
          <el-radio-button :value="43200">1 imp / 12 h</el-radio-button>
          <el-radio-button :value="28800">1 imp / 8 h</el-radio-button>
          <el-radio-button :value="21600">1 imp / 6 h</el-radio-button>
        </el-radio-group>
      </div>
    </el-form-item>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import useValidation from '@/hooks/useValidation'
import {
  CreativeValidationErrorsKey,
  FeedValidationErrorsKey
} from '@/api/types'
import useResize from '@/hooks/useResize'
import IconWithHint from '@/components/IconWithHint.vue'

export default defineComponent({
  name: 'RefreshRateInput',
  components: {
    IconWithHint
  },
  props: {
    modelValue: {
      type: [Number],
      default: 0
    },
    entity: {
      type: String,
      default: 'creative'
    }
  },
  setup(props, context) {
    const validationErrorsKey =
      props.entity === 'creative'
        ? CreativeValidationErrorsKey
        : FeedValidationErrorsKey
    const { validationErrors } = useValidation(validationErrorsKey)
    const errors = validationErrors()
    const { isDesktop } = useResize()
    const refreshRate = ref(props.modelValue)
    const baseHint = `
      Number of seconds that must pass<br>
      between two consecutive impressions<br>
      from the same IP and User Agent.<br>
      Use this setting only if you want to get unique traffic.<br>
      If you need more traffic, you may skip this setting.
    `
    const entityHintTexts: Record<string, string> = {
      feed: `You still may see impressions within Frequency<br>
               Capping impressions from the same IP and UserAgent<br>
               because we mark visitors with the same IP and<br>
               UserAgent only after the first ad impression.<br>
               Your feed might win the auction several times<br>
               before the first ad impression.`
    }
    const hint = [baseHint, entityHintTexts[props.entity] || ''].join('<br>')

    const input = () => {
      context.emit('update:modelValue', refreshRate.value)
    }

    const dynamicHintText = computed(() => {
      if (refreshRate.value === 0) {
        return 'No frequency capping'
      }
      const hours = Math.floor(refreshRate.value / 3600)
      return `1 imp / ${hours}h = max 1 ad impression on a spot to the same visitor in ${hours} hours`
    })

    return {
      errors,
      input,
      isDesktop,
      refreshRate,
      hint,
      dynamicHintText
    }
  }
})
</script>

<style lang="scss" scoped>
.frequency-capping-item {
  :deep(.el-form-item__content) {
    flex-direction: column;
    align-items: flex-start;
  }
}

:deep(.el-radio-button__orig-radio:checked + .el-radio-button__inner) {
  box-shadow: none;
  background-color: #eceef5;
  border-color: #dddfe6;
  color: #303133;
}

:deep(.el-radio-button__inner:hover) {
  color: #303133;
  background-color: #f5f8fd;
}

.is-error + .el-form-item {
  margin-top: 25px;
}

.input-hint {
  display: block;
  margin-bottom: 10px;
  color: #909399;
  font-size: 13px;
}

.radio-wrapper {
  display: block;
}
</style>
